import {
  OneOfElements,
  OneOfPropertyElements,
} from '@shared/models/project.model';
import {
  ElementPropertyType,
  IElementProperty,
} from '@shared/models/element-property.model';
import { cloneReplaceAt } from '@shared/helpers/array.helpers';
import {
  getElementProperties,
  isElementPropertySourceCategory,
  setPropertyCount,
} from '../../element-property.helpers';
import {
  isElement,
  isOneOfPropertyElements,
} from '../../recursive-element.helpers';
import { applyConditionalCategoryProperties } from '../../element-category.helpers';
import { ProjectChangeType } from '../update-engine.models';
import type { ProjectChangeMap } from '../update-engine.models';

type PropertyCountChange =
  ProjectChangeMap[ProjectChangeType.ElementPropertyCount];

/** Property types whose count can drive category template conditionals. */
const CONDITIONAL_DRIVING_TYPES: ReadonlySet<ElementPropertyType> = new Set([
  ElementPropertyType.Select,
  ElementPropertyType.Switch,
]);

/**
 * Build a partial element change for ElementPropertyCount (e.g. data table sync).
 * Updates the property's `count` in element.properties.
 *
 * When the affected property is a category-source Select / Switch (i.e. it can drive
 * conditional category rules), the resulting properties array is re-derived inline via
 * {@link applyConditionalCategoryProperties}. This unlocks dependent rows (e.g.
 * `heat_source` once `heating_type=heat_pump`) within the same batch so subsequent
 * property writes target the right names instead of being dropped.
 *
 * @param element - Current element state
 * @param change - Count payload
 * @returns Partial with updated properties array, or undefined if not an element / property not found
 */
export const elementPropertyCountToChange = (
  element: OneOfElements,
  change: PropertyCountChange,
): Partial<OneOfPropertyElements> | undefined => {
  if (!isOneOfPropertyElements(element)) return undefined;

  const props = getElementProperties(element);
  const index = props.findIndex((p) => p.name === change.propertyName);
  const prop = props[index];
  if (index === -1 || !prop) return undefined;

  // skipValidation: pipeline transforms must not run options-validation against baseline
  // (options can lag category-driving edits on a proposal until enrichment runs).
  const updatedProp = setPropertyCount(prop, change.count, 'count', {
    skipValidation: true,
  });

  const updatedProps = cloneReplaceAt(
    props,
    index,
    updatedProp as IElementProperty,
  );

  // Re-derive category-conditional rows so dependent properties show up immediately.
  // Skipping the rewrite for non-category or non-conditional types keeps the hot path lean.
  if (
    isElement(element) &&
    isElementPropertySourceCategory(prop) &&
    prop.type !== undefined &&
    CONDITIONAL_DRIVING_TYPES.has(prop.type)
  ) {
    const recomputed = applyConditionalCategoryProperties({
      ...element,
      properties: updatedProps,
    });
    return { properties: getElementProperties(recomputed) };
  }

  return { properties: updatedProps };
};
